import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import { fetchCompanies } from "@/api/fetchCompanies/fetchCompaniesApi";
import { fetchProductsHistoryReportCategories } from "../../api/fetchProductsHistoryReportCategories/fetchProductsHistoryReportCategoriesApi";
import type { ProductsHistoryReportFilters } from "../../hooks/useProductsHistoryReport";

type Option = { label: string; value: string };

interface ProductsHistoryReportFiltersProps {
  filters: ProductsHistoryReportFilters;
  isExporting: boolean;
  onFilterChange: (
    key: keyof ProductsHistoryReportFilters,
    value: string | string[] | number[] | undefined,
  ) => void;
  onExportCsv: () => void;
}

const STATUS_OPTIONS: Option[] = [
  { value: "paid", label: "Paid" },
  { value: "shipped", label: "Shipped" },
  { value: "completed", label: "Completed" },
];

const ProductsHistoryReportFilters: React.FC<
  ProductsHistoryReportFiltersProps
> = ({ filters, isExporting, onFilterChange, onExportCsv }) => {
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm ?? "");
  const [companyValue, setCompanyValue] = useState<string[]>(
    (filters.companyIds ?? []).map(String),
  );
  // Labels for the selected companies, kept separate from search results so a
  // selected chip keeps its name even when it falls outside the current query.
  const [companySelected, setCompanySelected] = useState<Option[]>([]);
  const [companyResults, setCompanyResults] = useState<Option[]>([]);
  const [companySearch, setCompanySearch] = useState("");
  const [categoryIds, setCategoryIds] = useState<string[]>(
    (filters.categoryIds ?? []).map(String),
  );
  const [statuses, setStatuses] = useState<string[]>(filters.statuses ?? []);
  const [orderDateFrom, setOrderDateFrom] = useState(
    filters.orderDateFrom ?? "",
  );
  const [orderDateTo, setOrderDateTo] = useState(filters.orderDateTo ?? "");
  const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const debouncedCompanySearch = useDebouncedValue(companySearch, 400);
  const isMounted = useRef(false);

  // The categories endpoint is unpaged and returns every row, so a single
  // fetch is enough -- no need for the loadAllFilterOptions pager.
  useEffect(() => {
    fetchProductsHistoryReportCategories().then((response) => {
      setCategoryOptions(
        response.data.map((category) => ({
          label: category.name,
          value: String(category.id),
        })),
      );
    });
  }, []);

  useEffect(() => {
    let active = true;
    fetchCompanies({
      searchTerm: debouncedCompanySearch || undefined,
      page: 1,
      pageSize: 50,
      sortBy: "companyName",
      sortOrder: "ASC",
    }).then((response) => {
      if (!active) return;
      setCompanyResults(
        response.data.companies.map((company) => ({
          label: company.companyName,
          value: String(company.id),
        })),
      );
    });
    return () => {
      active = false;
    };
  }, [debouncedCompanySearch]);

  // Union of selected + current results so chips always resolve a label and
  // search hits remain selectable.
  const companyOptions = useMemo(() => {
    const byValue = new Map<string, Option>();
    [...companySelected, ...companyResults].forEach((option) => {
      byValue.set(option.value, option);
    });
    return Array.from(byValue.values());
  }, [companySelected, companyResults]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFilterChange("searchTerm", debouncedSearchTerm || undefined);
  }, [debouncedSearchTerm, onFilterChange]);

  const handleCompanyChange = (value: string[]) => {
    setCompanyValue(value);
    setCompanySelected(
      companyOptions.filter((option) => value.includes(option.value)),
    );
    onFilterChange("companyIds", value.length ? value.map(Number) : undefined);
  };

  const handleCategoryChange = (value: string[]) => {
    setCategoryIds(value);
    onFilterChange("categoryIds", value.length ? value.map(Number) : undefined);
  };

  const handleStatusChange = (value: string[]) => {
    setStatuses(value);
    onFilterChange("statuses", value.length ? value : undefined);
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderDateFrom(e.target.value);
    onFilterChange("orderDateFrom", e.target.value || undefined);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderDateTo(e.target.value);
    onFilterChange("orderDateTo", e.target.value || undefined);
  };

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setCompanyValue([]);
    setCompanySelected([]);
    setCompanySearch("");
    setCategoryIds([]);
    setStatuses([]);
    setOrderDateFrom("");
    setOrderDateTo("");
    onFilterChange("searchTerm", undefined);
    onFilterChange("companyIds", undefined);
    onFilterChange("categoryIds", undefined);
    onFilterChange("statuses", undefined);
    onFilterChange("orderDateFrom", undefined);
    onFilterChange("orderDateTo", undefined);
  }, [onFilterChange]);

  const hasActiveFilters =
    searchTerm ||
    companyValue.length ||
    categoryIds.length ||
    statuses.length ||
    orderDateFrom ||
    orderDateTo;

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="searchTerm">Search</Label>
          <Input
            className="mt-1.5 bg-white"
            id="searchTerm"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Product, company..."
            type="text"
            value={searchTerm}
          />
        </div>

        <div>
          <Label>Company</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleCompanyChange}
            onSearch={setCompanySearch}
            options={companyOptions}
            placeholder="All Companies"
            searchPlaceholder="Search companies..."
            value={companyValue}
          />
        </div>

        <div>
          <Label>Category</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleCategoryChange}
            options={categoryOptions}
            placeholder="All Categories"
            value={categoryIds}
          />
        </div>

        <div>
          <Label>Status</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
            placeholder="All Statuses"
            value={statuses}
          />
        </div>

        <div>
          <Label htmlFor="orderDateFrom">Order Date From</Label>
          <Input
            className="mt-1.5 bg-white"
            id="orderDateFrom"
            onChange={handleDateFromChange}
            type="date"
            value={orderDateFrom}
          />
        </div>

        <div>
          <Label htmlFor="orderDateTo">Order Date To</Label>
          <Input
            className="mt-1.5 bg-white"
            id="orderDateTo"
            onChange={handleDateToChange}
            type="date"
            value={orderDateTo}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button
            className="border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            disabled={!hasActiveFilters}
            onClick={handleClearFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            disabled={isExporting}
            onClick={onExportCsv}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductsHistoryReportFilters;
